import { ZyxClient } from '@zyx/client';
import { config } from './config';
import { classifyProbeFailure, type Connection, type ProbeOutcome } from './connection';

/**
 * The web app's handle on a service. A factory rather than a singleton: which service
 * the app talks to is chosen at runtime. Components reach it through `useApi()`.
 *
 * With a token, `credentials: 'include'` keeps the cookie `POST /session` sets, which
 * is what lets `<img>` and `<video>` load cached files. Without one there is no cookie,
 * and asking for credentials would only make the service's CORS answer stricter.
 */
export function createApi(connection: Connection): ZyxClient {
  return new ZyxClient({
    baseUrl: connection.apiUrl,
    token: connection.token,
    timeoutMs: config.requestTimeoutMs,
    credentials: connection.token === undefined ? 'omit' : 'include',
  });
}

/**
 * Checks a candidate connection before the app commits to it. `/ping` is public, so it
 * separates "nothing answered" from "something answered and refused you"; an
 * authenticated call then reveals whether a token is wanted. On success the session
 * cookie is set, for the images.
 */
export async function probeConnection(connection: Connection): Promise<ProbeOutcome> {
  const client = createApi(connection);
  try {
    await client.ping();
  } catch (error) {
    return classifyProbeFailure(error, connection.token !== undefined) === 'unreachable' ? 'unreachable' : 'error';
  }

  try {
    await client.listMovies({ limit: 1 });
    await client.startSession();
    return 'ok';
  } catch (error) {
    return classifyProbeFailure(error, connection.token !== undefined);
  }
}
